<template>
  <div>
    <div>
      <b-button
        v-if="(!productPages || productPages.length <= 1) && !inDesignMode"
        :variant="tab == 0 ? 'branded' : 'link'"
        :class="tab == 0 ? '' : 'text-branded'"
        @click="tab = 0"
      >
        {{ page.name }}
      </b-button>
      <b-dropdown
        v-if="!!productPages && productPages.length > 1 || inDesignMode"
        split
        :text="page.name"
        :variant="tab == 0 ? 'branded' : 'link'"
        :class="tab == 0 ? '' : 'text-branded'"
        menu-class="constrained"
        @click="tab = 0"
      >
        <b-dropdown-item
          v-for="p in productPages" :key="p.id"
          :href="isIframed ? p.iframe_url : p.url"
          link-class="nested nav-package description"
          :active="p.id == page.id"
        >
          <span :class="p.permission == 'admin' ? 'text-danger' : ''">{{ p.name }}</span>
        </b-dropdown-item>
        <b-dropdown-divider v-if="inDesignMode"></b-dropdown-divider>
        <b-dropdown-item
          v-if="inDesignMode"
          link-class="nested nav-package description"
          @click="onClickSettings"
        >
          {{ trans('settings') }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="inDesignMode"
          link-class="nested nav-package description"
          @click="onClickReorderItems"
        >
          {{ trans('reorder') }}
        </b-dropdown-item>
      </b-dropdown>
      <cart-link
        v-if="!!invoice"
        :invoice="invoice"
        :active="tab == 1"
        @click="tab = 1"
      ></cart-link>
    </div>
    <div v-show="tab == 0" class="mt-3">
      <b-nav v-if="!!allTagsWithPotentialNewTag && allTagsWithPotentialNewTag.length > 0" small pills class="mb-3 short">
        <b-nav-item
          :active="!tag"
          @click="tag = null"
          class="mr-2"
        >
          {{ trans('all') }}
        </b-nav-item>
        <b-nav-item v-for="t in allTagsWithPotentialNewTag" :key="t.id" :active="tag == t.name" @click="tag = t.name" class="mr-2">
          {{ t.name }}
        </b-nav-item>
        <b-nav-item v-if="inDesignMode && !new_tag" class="mr-2" @click="onClickNewTag">
          <i class="fa fa-fw fa-plus"></i>
        </b-nav-item>
      </b-nav>
      <slot></slot>
    </div>
    <div v-if="!!invoice" v-show="tab == 1" class="mt-3">
      <b-card>
        <cart-content
          v-if="!invoice || !invoice.finalized_at"
          :organization="organization"
          :invoice="invoice"
          show-close
          @invoice="invoice = $event"
          @invoiceempty="onEmptyCart"
          @close="tab = 0"
          @click="onCheckout"
        ></cart-content>
        <div v-if="showAdminPayment">
          <br/>
          <admin-options
            variant="outline-danger"
            :organization="organization"
            :client-secret="client_secret"
            @client-secret="client_secret = $event"
            :invoice="invoice"
            @invoice="invoice = $event"
          ></admin-options>
        </div>
        <confirmation-card
          v-if="!!invoice && !!invoice.finalized_at"
          :organization="organization"
          :invoice="invoice"
          :recipient="invoice.recipient"
        ></confirmation-card>
      </b-card>
    </div>

    <b-button
      id="floating-cart"
      v-if="tab == 0 && !!invoice && invoice.invoice_items.length > 0"
      variant="branded"
      class="shadow"
      :href="`${iframeUrlPrefix}/org/${this.organization.id}/checkout${session}`"
    >
      <i class="fa fa-fw fa-shopping-cart"></i>
      {{ trans('checkout') }}
    </b-button>
    <product-quick-edit-modal
      v-if="inDesignMode"
      :organization="organization"
      :product="product"
      :block="block"
      :tag="tag"
      :all-tags="allTagsWithPotentialNewTag"
      :all-taxes="allTaxes"
      @saved="onUpdateProduct"
    ></product-quick-edit-modal>
    <product-quick-edit-modal
      v-if="inDesignMode"
      id="product-quick-add-modal"
      :organization="organization"
      :block="block"
      :tag="tag"
      :all-tags="allTagsWithPotentialNewTag"
      :all-taxes="allTaxes"
    ></product-quick-edit-modal>
    <page-products-modal
      v-if="inDesignMode"
      id="tabs-page-products-modal"
      :organization="organization"
      :page="page"
      :block="block || undefined"
      @saved="onSavedSettings"
    ></page-products-modal>
    <reorder-modal
      v-if="inDesignMode"
      id="reorder-products-modal"
      :prop-items="visibleProducts"
      :save-url="`/organization/${organization.id}/product/reorder`"
      @saved="onRefreshAfterReorder"
    >
      <template #item="{item}">
        {{ item.name }}
        <strong>{{ currency(item.price) }}</strong>
        <small v-if="!!item.short_description" class="d-block" v-html="item.short_description"></small>
      </template>
    </reorder-modal>
    <textarea-modal
      id="new-tag-modal"
      type="input"
      :title="trans('create_new_tag')"
      :label="trans('tag')"
      :value="!!new_tag ? new_tag.name : null"
      :validation-fn="validateNewTag"
      @input="onSaveNewTag"
      @cancel="onCancelNewTag"
    ></textarea-modal>
  </div>
</template>

<style>
  #floating-cart {
    position: fixed;
    bottom: 2em;
    right: 2em;
  }
  .nav-pills.small.short a.nav-link {
    padding: 0.25rem 1rem;
  }
</style>

<script>
  import ProductAddToCart from './ProductAddToCart'
  import CartLink from './CartLink'
  import CartContent from './CartContent'
  import AdminOptions from './CheckoutAdminOptions'
  import ConfirmationCard from './CheckoutConfirmation'
  import ProductQuickEditModal from './../microsite-designer/ProductQuickEditModal'
  import PageProductsModal from './../microsite-designer/PageProductsModal'
  import ReorderModal from './../../../common/components/ReorderModal'
  import TextareaModal from './../../../common/components/TextareaModal'
  import querystring from 'querystring'

  export default {
    props: {
      organization: Object,
      page: Object,
      block: Object, // Will be null when the products page
      productPages: Array,
      products: Array,
      allTags: Array,
      allTaxes: Array,
      adminCanModifyPrice: Boolean,
    },
    components: {
      ProductAddToCart,
      CartLink,
      CartContent,
      AdminOptions,
      ConfirmationCard,
      ProductQuickEditModal,
      PageProductsModal,
      ReorderModal,
      TextareaModal,
    },
    data() {
      let query = querystring.parse(window.location.search.substring(1))
      let tag = query.tag
      if (!!tag) {
        let found = _.find(this.allTags || [], {name: tag})
        if (!found) tag = null
      }
      if (!tag) {
        tag = null
      }

      return {
        trans: trans,
        moment: moment,
        currency: _.currency,
        isIframed: _.isIframed(),
        iframeUrlPrefix: _.iframeUrlPrefix,
        promoCodeReduction: _.promoCodeReduction,
        busy: false,
        error: null,
        tab: 0,
        invoice: undefined,
        client_secret: null,
        vms: [],
        tag: tag,
        new_tag: null,
        product: null,
      }
    },
    computed: {
      session() {
        let session = ''
        if (window.session_id) {
          session = `?_session=${window.session_id}`;
        }
        return session
      },
      showAdminOptions() {
        if (!this.invoice) return false
        return this.organization.auth_user &&
          this.organization.auth_user.microsite_permissions.has_admin_payments &&
          !!this.invoice.admin
      },
      showAdminPayment() {
        return this.showAdminOptions &&
          this.invoice.total > 0 &&
          this.invoice.is_no_client &&
          this.invoice.status == 'new'
      },
      inDesignMode() {
        return !!this.organization.auth_user && this.organization.is_editing_microsite
      },
      allTagsWithPotentialNewTag() {
        let tags = _.reduce(this.allTags || [], (tags, tag) => {
          if (_.some(this.products, product => {
            return _.find(product.tags, {name: tag.name})
          })) {
            tags.push(tag)
          }
          return tags
        }, [])
        if (!!this.new_tag) tags.push(this.new_tag)
        return tags
      },
      visibleProducts() {
        return _.reduce(this.products, (visible, p) => {
          const show = !!this.tag ? _.find(p.tags, {name: this.tag}) : true
          if (show) visible.push(p)
          return visible
        }, [])
      },
    },
    watch: {
      invoice() {
        _.each(this.vms, vm => {
          vm.invoice = this.invoice
        });
      },
      tag() {
        this.toggleVisibleProductsBySelectedTag()
        let query = ''
        if (!!this.tag) query = `?tag=${encodeURIComponent(this.tag)}`
        window.history.replaceState(null, null, window.location.href.split('?')[0] + query)
      },
      tab() {
        if (this.tab == 0 && !!this.invoice && !!this.invoice.finalized_at) {
          this.invoice = undefined
        }
      },
    },
    methods: {
      onClickNewTag() {
        this.new_tag = {
          id: null,
          name: '',
          slug: ''
        }
        this.$bvModal.show('new-tag-modal')
      },
      validateNewTag(name) {
        return !_.isEmpty(name) && !!name.trim()
      },
      onSaveNewTag(name) {
        this.new_tag.name = name
        this.new_tag.slug = _.slugify(name)
        this.$nextTick(() => {
          this.tag = this.new_tag.name
        })
      },
      onCancelNewTag() {
        this.new_tag = null
      },
      onClickReorderItems() {
        this.$bvModal.show('reorder-products-modal')
      },
      onRefreshAfterReorder() {
        window.location.reload()
      },
      onClickSettings() {
        this.$bvModal.show('tabs-page-products-modal')
      },
      onSavedSettings() {
        window.location.reload()
      },
      toggleVisibleProductsBySelectedTag() {
        _.each(this.products, p => {
          const el = document.getElementById('product-card-' + p.id).parentNode
          const show = this.tag ? _.find(p.tags, {name: this.tag}) : true
          el.style.display = show ? 'block' : 'none'
        })
      },
      onCheckout() {
        window.location = `${_.iframeUrlPrefix}/org/${this.organization.id}/checkout${this.session}#form`
      },
      onEmptyCart() {
        this.tab = 0
        this.$nextTick(() => {
          this.invoice = null
        })
      },
      loadCart() {
        this.busy = true
        this.error = null
        return axios.get(`/org/${this.organization.id}/cart`)
        .then(response => {
          this.busy = false
          this.invoice = !_.isEmpty(response.data) ? response.data : null
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      mountProducts() {
        const ProductCtor = Vue.extend(ProductAddToCart)
        _.each(this.products, product => {
          const vm = new ProductCtor({
            parent: this,
            el: '#product-' + product.id,
            propsData: {
              organization: this.organization,
              product: product,
              adminCanModifyPrice: this.adminCanModifyPrice,
            },
            data: {
              invoice: this.invoice,
            },
            mounted() {
              let edit_btn_el = document.getElementById('product-quick-edit-btn-'+this.product.id)
              if (!!edit_btn_el) edit_btn_el.onclick = () => {
                this.$emit('quick-edit', product.id)
              }
            },
          })
          .$on('invoice', invoice => {
            this.invoice = invoice
          })
          .$on('quick-edit', product_id => {
            this.product = _.find(this.products, {id: product_id})
            this.$bvModal.show('product-quick-edit-modal')
          })
          this.vms.push(vm)
        })
      },
      onUpdateProduct(product) {
        this.product = null
        let i = _.findIndex(this.products, {id: product.id})
        if (i === -1) return
        this.$set(this.products, i, product)
        const el = document.getElementById('product-card-' + product.id)
        if (!el) return
        const img_el = el.querySelector('img.card-img-top')
        if (img_el) {
          img_el.src = product.image_url
          img_el.style.display = !!product.image_url ? 'flex' : 'none'
        }
        const name_el = el.querySelector('.card-title')
        if (name_el) name_el.innerText = product.name
        const short_description_el = el.querySelector('.short-description')
        if (short_description_el) short_description_el.innerText = product.short_description
        const description_toggle_el = el.querySelector('.toggle-description')
        const description_el = el.querySelector('.description')
        if (description_toggle_el && description_el) {
          description_toggle_el.style.display = !!product.description ? 'inline' : 'none'
          description_el.innerHTML = product.description
          if (!product.description && description_el.className.indexOf('show') >= 0)
            description_el.className = description_el.className.replace('show', '')
        }
        const price_el = el.querySelector('.price')
        if (price_el) price_el.innerText = _.currency(product.price)
        const discount_el = el.querySelector('.discount')
        if (!!product.promo_code) {
          price_el.style.textDecoration = 'line-through'
          const reduction = _.promoCodeReduction(product.promo_code, product.price)
          const discount = product.display_promo_dollar ?
            _.currency(reduction) :
            numeral(reduction/product.price).format('0%')
          discount_el.style.display = 'block'
          discount_el.querySelector('span.badge').innerText = `${discount} ${trans('discount_off').toLowerCase()}`
        } else {
          price_el.style.textDecoration = 'inherit'
          discount_el.style.display = 'none'
        }
        this.toggleVisibleProductsBySelectedTag()
      },
    },
    mounted() {
      window.productsTabs = this
      let el = document.getElementById('server-side-pills')
      if (el) el.remove()
      this.toggleVisibleProductsBySelectedTag()
      this.loadCart().then(this.mountProducts)
    },
  }
</script>
